import { CompositeFilter, Filter } from "./types";

// // // //

/**
 * areFiltersIdentical
 * Recursively checks if two `Filter` objects are identical
 * @param {Filter} currentFilter - The current filter object to compare.
 * @param {Filter} latestFilter - The latest filter object to compare.
 * @returns {boolean} - `true` if the filters are identical, `false` otherwise.
 */
export function areFiltersIdentical(props: {
    currentFilter: Filter | undefined;
    latestFilter: Filter | undefined;
}): boolean {
    const { currentFilter, latestFilter } = props;

    if (!currentFilter && !latestFilter) {
        return true; // Both filters are undefined, so they are considered identical
    }

    if (!currentFilter || !latestFilter) {
        return false; // One of the filters is undefined while the other is not, so they are considered different
    }

    if (currentFilter.operator !== latestFilter.operator) {
        return false; // The operator of the filters is different, so they are considered different
    }

    // Check if the filter is a BasicFilter
    if ("field" in currentFilter && "value" in currentFilter) {
        // Check if all the properties of the BasicFilter are identical
        return (
            currentFilter.field === latestFilter.field &&
            currentFilter.value === latestFilter.value
        );
    }

    // The filter is a CompositeFilter
    const currentCompositeFilter = currentFilter as CompositeFilter;
    const latestCompositeFilter = latestFilter as CompositeFilter;

    if (
        currentCompositeFilter.filters.length !==
        latestCompositeFilter.filters.length
    ) {
        return false; // The number of nested filters is different, so they are considered different
    }

    // Check if each nested filter is identical recursively
    for (let i = 0; i < currentCompositeFilter.filters.length; i++) {
        const currentNestedFilter = currentCompositeFilter.filters[i];
        const latestNestedFilter = latestCompositeFilter.filters[i];

        if (
            !areFiltersIdentical({
                currentFilter: currentNestedFilter,
                latestFilter: latestNestedFilter,
            })
        ) {
            return false; // One of the nested filters is not identical, so the composite filters are considered different
        }
    }

    return true; // All the properties and nested filters are identical, so the filters are considered identical
}
